import {
  BALANCE_DELTA,
  Box,
  Button,
  ButtonVariants,
  Dropdown,
  EMPTY_OBJECT,
  FormControlSizes,
  HStack,
  IconName,
  InlineFormattedNumber,
  MarketAccountTypeEnum,
  POSITION,
  PositionUpdateSourceEnum,
  SearchSelect,
  TableSize,
  Text,
  getPositionAmount,
  isFuture,
  isOption,
  isPerpetualSwap,
  useCurrency,
  useDropdownPopper,
  useMarketAccountsContext,
  useMarketsContext,
  useObservableValue,
  useSubscription,
  type MarketAccount,
  type OptionBalance,
  type Position,
  type RequestStream,
  type Security,
} from '@talos/kyoko';
import { compact } from 'lodash-es';
import { useCallback, useMemo, useRef, useState } from 'react';
import { map } from 'rxjs';
import { ContractDetails } from '../../../../components/ContractDetails';
import { useDisplaySettings } from '../../../../providers/DisplaySettingsProvider';
import { POSITIONS_BLOTTER_CONVERSION_TOLERANCE } from '../../../Blotters/PositionsV3/tokens';

interface MarketSummaryProps {
  security: Security;
}

const getMarketAccountLabel = (marketAccount: MarketAccount) => marketAccount.DisplayName;

export function MarketSummary({ security }: MarketSummaryProps) {
  const { marketAccountsByMarket, isMarketAccountActive } = useMarketAccountsContext();
  const { marketDisplayNameByName } = useMarketsContext();

  const marketAccounts = useMemo(() => {
    return (security.Markets ?? []).reduce((acc: MarketAccount[], market) => {
      acc = acc
        .concat(marketAccountsByMarket.get(market) || [])
        .filter(ma => isMarketAccountActive(ma) && ma?.Type === MarketAccountTypeEnum.Trading);
      return acc;
    }, []);
  }, [security.Markets, marketAccountsByMarket, isMarketAccountActive]);

  const [selectedMarketAccount, setSelectedMarketAccount] = useState<MarketAccount | undefined>(marketAccounts[0]);

  const handleOnMarketAccountChange = useCallback((marketAccount: MarketAccount | undefined) => {
    setSelectedMarketAccount(marketAccount);
  }, []);

  const showPerpsAndFuturesStats = isPerpetualSwap(security) || isFuture(security);
  const showOptionsStats = isOption(security);

  return (
    <HStack alignItems="baseline" justifyContent="flex-start" gap="spacing24" w="100%">
      <SearchSelect
        options={marketAccounts}
        getLabel={getMarketAccountLabel}
        onChange={handleOnMarketAccountChange}
        selection={selectedMarketAccount}
        size={FormControlSizes.Small}
        prefix={
          <Text mr="spacing4" color="colors.gray.090" fontWeight="fontWeightMedium">
            Market Account
          </Text>
        }
        gridTemplateColumns="max-content auto min-content"
        style={{ width: 'fit-content' }}
      />

      <HStack gap="spacing4">
        <Text size="fontSizeSmall" color="colors.gray.090">
          Market
        </Text>
        <Text size="fontSizeSmall" fontWeight="fontWeightMedium" data-testid="marjet" color="colors.gray.100">
          {marketDisplayNameByName.get(selectedMarketAccount?.Market ?? '')}
        </Text>
      </HStack>
      {showPerpsAndFuturesStats && <PerpsAndFuturesStats security={security} marketAccount={selectedMarketAccount} />}
      {showOptionsStats && <OptionsStats security={security} marketAccount={selectedMarketAccount} />}
      <HStack gap="spacing8" flex="auto" justifyContent="flex-end">
        <ContractDetailsCard security={security} marketAccount={selectedMarketAccount} />
      </HStack>
    </HStack>
  );
}

interface StatsProps {
  security: Security;
  marketAccount: MarketAccount | undefined;
}

function PerpsAndFuturesStats({ security, marketAccount }: StatsProps) {
  return <PositionStats security={security} marketAccount={marketAccount} />;
}

function OptionsStats({ security, marketAccount }: StatsProps) {
  const balanceRequest: RequestStream = useMemo(() => {
    const MarketAccounts = marketAccount?.Name ? [marketAccount.Name] : undefined;
    return {
      name: BALANCE_DELTA,
      tag: 'OptionsStats',
      Markets: marketAccount ? [marketAccount.Market] : undefined,
      MarketAccounts: MarketAccounts,
      Currencies: [security.BaseCurrency],
    };
  }, [marketAccount, security.BaseCurrency]);
  const { data: balancesObs } = useSubscription<Partial<OptionBalance>>(balanceRequest);
  const balancesData =
    useObservableValue(() => balancesObs.pipe(map(json => json.data.at(0))), [balancesObs]) || EMPTY_OBJECT;
  const balanceCurrency = useCurrency(balancesData.Currency);

  return (
    <>
      <HStack gap="spacing4">
        <Text size="fontSizeSmall" color="colors.gray.090">
          Initial Margin
        </Text>
        <Text size="fontSizeSmall" fontWeight="fontWeightMedium" data-testid="initial-margin" color="colors.gray.100">
          {balancesData.InitialMargin ? (
            <InlineFormattedNumber
              number={balancesData.InitialMargin}
              currency={balancesData.Currency}
              increment={balanceCurrency?.DefaultIncrement}
            />
          ) : (
            'n/a'
          )}
        </Text>
      </HStack>
      <HStack gap="spacing4">
        <Text size="fontSizeSmall" color="colors.gray.090">
          Maintenance Margin
        </Text>
        <Text
          size="fontSizeSmall"
          fontWeight="fontWeightMedium"
          data-testid="maintenance-margin"
          color="colors.gray.100"
        >
          {balancesData.MaintenanceMargin ? (
            <InlineFormattedNumber
              number={balancesData.MaintenanceMargin}
              currency={balancesData.Currency}
              increment={balanceCurrency?.DefaultIncrement}
            />
          ) : (
            'n/a'
          )}
        </Text>
      </HStack>
      <HStack gap="spacing4">
        <Text size="fontSizeSmall" color="colors.gray.090">
          Available Balance
        </Text>
        <Text
          size="fontSizeSmall"
          fontWeight="fontWeightMedium"
          data-testid="available-balance"
          color="colors.gray.100"
        >
          {balancesData.AvailableAmount ? (
            <InlineFormattedNumber
              number={balancesData.AvailableAmount}
              currency={balancesData.Currency}
              increment={balanceCurrency?.DefaultIncrement}
            />
          ) : (
            'n/a'
          )}
        </Text>
      </HStack>
      <PositionStats security={security} marketAccount={marketAccount} />
    </>
  );
}

function PositionStats({ security, marketAccount }: StatsProps) {
  const isPerpOrFuture = isPerpetualSwap(security) || isFuture(security);
  const { homeCurrency } = useDisplaySettings();
  const positionRequest = useMemo(
    () => ({
      name: POSITION,
      tag: 'PositionStats',
      Source: PositionUpdateSourceEnum.Gateway,
      Symbol: security.Symbol,
      MarketAccounts: marketAccount ? [marketAccount.Name] : undefined,
      AssetTypes: [security.ProductType],
      EquivalentCurrency: homeCurrency,
      Tolerance: POSITIONS_BLOTTER_CONVERSION_TOLERANCE,
    }),
    [security.Symbol, security.ProductType, marketAccount, homeCurrency]
  );
  const { data: positionObs } = useSubscription<Partial<Position>>(positionRequest);
  const positionData =
    useObservableValue(() => positionObs.pipe(map(json => json.data.at(0))), [positionObs]) || EMPTY_OBJECT;

  let value = positionData.Amount ?? '0';
  let currency = positionData.Asset;
  if (isPerpOrFuture) {
    // Display positions for future and perps with notional multiplier always in base or quote ccy
    const positionAmount = getPositionAmount(value, security);
    value = positionAmount.value;
    currency = positionAmount.currency;
  }
  const positionCurrency = useCurrency(currency);
  const avgCostCurrency = useCurrency(positionData.AvgCostCurrency);
  const pnlCurrency = useCurrency(positionData.PnLCurrency);

  return (
    <>
      <HStack gap="spacing4">
        <Text size="fontSizeSmall" color="colors.gray.090">
          Position
        </Text>
        <Text size="fontSizeSmall" fontWeight="fontWeightMedium" data-testid="position" color="colors.gray.100">
          {value ? (
            <InlineFormattedNumber
              number={value}
              currency={positionCurrency?.Symbol}
              increment={positionCurrency?.DefaultIncrement}
            />
          ) : (
            'n/a'
          )}
        </Text>
      </HStack>
      {isPerpOrFuture && (
        <>
          <HStack gap="spacing4">
            <Text size="fontSizeSmall" color="colors.gray.090">
              Avg Cost
            </Text>
            <Text size="fontSizeSmall" fontWeight="fontWeightMedium" data-testid="avg-cost" color="colors.gray.100">
              {positionData.AvgCost ? (
                <InlineFormattedNumber
                  number={positionData.AvgCost}
                  currency={avgCostCurrency?.Symbol}
                  increment={avgCostCurrency?.DefaultIncrement}
                />
              ) : (
                'n/a'
              )}
            </Text>
          </HStack>
          <HStack gap="spacing4">
            <Text size="fontSizeSmall" color="colors.gray.090">
              Unrealized PnL
            </Text>
            <Text
              size="fontSizeSmall"
              fontWeight="fontWeightMedium"
              data-testid="unrealized-pnl"
              color="colors.gray.100"
            >
              {positionData.UnrealizedPnL ? (
                <InlineFormattedNumber
                  number={positionData.UnrealizedPnL}
                  currency={pnlCurrency?.Symbol}
                  increment={pnlCurrency?.DefaultIncrement}
                />
              ) : (
                'n/a'
              )}
            </Text>
          </HStack>
        </>
      )}
    </>
  );
}

function ContractDetailsCard({ security, marketAccount }: StatsProps) {
  const [isOpen, setIsOpen] = useState(false);
  const selectedMarketAccounts = useMemo(() => compact([marketAccount?.Name]), [marketAccount]);
  const dropdownRef = useRef<HTMLButtonElement>(null);

  const handleClickOutside = useCallback((e: MouseEvent) => {
    const el = e.target;
    if (dropdownRef.current?.contains(el as Node | null)) {
      return false;
    }
    setIsOpen(false);
  }, []);

  const dropdownPopper = useDropdownPopper({
    isOpen,
    referenceElement: dropdownRef.current,
    dropdownWidth: '300px',
    dropdownPlacement: 'bottom-start',
    onClickOutside: handleClickOutside,
  });

  return (
    <>
      <Button
        variant={ButtonVariants.Default}
        size={FormControlSizes.Small}
        onClick={() => setIsOpen(prev => !prev)}
        endIcon={IconName.ChevronRight}
        ref={dropdownRef}
      >
        Contract Details
      </Button>
      <Dropdown {...dropdownPopper}>
        <Box p="spacing16">
          <ContractDetails
            symbol={security.Symbol}
            selectedMarketAccounts={selectedMarketAccounts}
            tag="DEEP_DIVE"
            size={TableSize.Default}
          />
        </Box>
      </Dropdown>
    </>
  );
}
